import { useCallback, useRef } from 'react';

import type {
  FeedbackConfig,
  MutationError,
  MutationWithToastConfig,
  ToastConfig,
  ToastId,
  ToastOptions,
  ToastType,
} from './useMutation.types';

import { useMutationHandler } from './useMutationHandler';

const DEFAULT_TOAST_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  error: 6000,
  loading: 0,
};

const titledToast = (title: string | undefined, type: ToastType): ToastOptions | undefined =>
  title ? { title, duration: DEFAULT_TOAST_DURATIONS[type] } : undefined;

export const useMutationWithFeedback = (feedback: FeedbackConfig, debug = false) => {
  const { handleMutation: coreHandleMutation, getDebugInfo, clearDebugInfo } = useMutationHandler(debug);
  const toastCache = useRef<Set<string>>(new Set());
  const { toast, dismiss } = feedback;

  const getToastKey = (options: ToastOptions): string => {
    return `${options.title}-${options.description || ''}`;
  };

  const shouldShowToast = useCallback((options: ToastOptions, dedupe: boolean): boolean => {
    if (!dedupe) return true;

    const key = getToastKey(options);

    if (toastCache.current.has(key)) {
      return false;
    }

    toastCache.current.add(key);

    setTimeout(() => {
      toastCache.current.delete(key);
    }, 5000);

    return true;
  }, []);

  const handleMutation = useCallback(
    async <T>(config: MutationWithToastConfig<T>): Promise<void> => {
      const { toast: toastConfig, ...coreConfig } = config;

      const defaultToastConfig: ToastConfig<T> = {
        dedupe: true,
        disabled: false,
      };

      const finalToastConfig = { ...defaultToastConfig, ...toastConfig };

      if (finalToastConfig.disabled) {
        return coreHandleMutation(coreConfig);
      }

      const showTerminalToast = (type: 'success' | 'error', options: ToastOptions, toastIdToUpdate?: ToastId) => {
        if (!shouldShowToast(options, finalToastConfig.dedupe || false)) {
          if (toastIdToUpdate != null) {
            dismiss(toastIdToUpdate);
          }

          return;
        }

        if (toastIdToUpdate != null) {
          toast(type, { ...options, id: toastIdToUpdate });

          return;
        }

        toast(type, options);
      };

      let loadingToastId: ToastId | undefined;

      const loadingOptions = titledToast(finalToastConfig.loading, 'loading');

      if (loadingOptions) {
        loadingToastId = toast('loading', loadingOptions);
      }

      return coreHandleMutation({
        ...coreConfig,
        onSuccess: async (result: T) => {
          const successOptions =
            finalToastConfig.successToast?.(result) ?? titledToast(finalToastConfig.success, 'success');

          if (successOptions) {
            showTerminalToast('success', successOptions, loadingToastId);
          } else if (loadingToastId != null) {
            dismiss(loadingToastId);
          }

          if (coreConfig.onSuccess) {
            await coreConfig.onSuccess(result);
          }
        },
        onError: (error: MutationError, result?: T) => {
          const errorOptions =
            finalToastConfig.errorToast?.(error, result) ?? titledToast(finalToastConfig.error, 'error');

          if (errorOptions) {
            showTerminalToast(
              'error',
              { ...errorOptions, description: errorOptions.description || error.message },
              loadingToastId,
            );
          } else if (loadingToastId != null) {
            dismiss(loadingToastId);
          }

          if (coreConfig.onError) {
            coreConfig.onError(error, result);
          }
        },
      });
    },
    [coreHandleMutation, shouldShowToast, toast, dismiss],
  );

  const clearToastCache = useCallback(() => {
    toastCache.current.clear();
  }, []);

  return {
    handleMutation,
    getDebugInfo,
    clearDebugInfo,
    clearToastCache,
  };
};
